/* Built by quiz/build.mjs. Do not edit: sources are quiz/src/score.js, quiz/src/ui.js, ebook/data/exposure_scores.csv, store/content/en/quiz.json (ids only). */
window.CFQuiz = (function () {
// AI Exposure Score. Contract: docs/superpowers/specs/2026-10-14-contracts.md §3.4.
// Inputs: answers { role_id, time {info,writing,data,customer,physical,people} (shares, sum 100), signals [bool x3], ai_use 0..3, experience_years 0..3 }
//         data { roles: rows of ebook/data/exposure_scores.csv }
// Output: { score 0..100, band, path, top_tasks, action_ids }
// No imports on purpose: quiz/build.mjs concatenates this file into the theme asset assets/cf-quiz.js.

const TASK_IDS = ['info', 'writing', 'data', 'customer', 'physical', 'people'];
const TASK_EXPOSURE = { info: 0.8, writing: 0.9, data: 0.85, customer: 0.6, physical: 0.05, people: 0.2 };
const ACTIONS_BY_PATH = {
  a: ['m4-day1', 'm4-day2', 'm4-day5'],
  b: ['m4-day1', 'm4-day2', 'm4-day5'],
  c: ['m4-day1', 'm4-day2', 'm4-day3'],
};
const WEIGHT_ROLE = 0.55;
const WEIGHT_TIME = 0.30;
const WEIGHT_SIGNALS = 0.15;
const PENALTY_AI_USE = 3;
const PENALTY_EXPERIENCE = 2;
// ms_applicability (Microsoft 2025) saturates at 0.5: the most exposed roles of the data set sit near it.
const MS_SATURATION = 0.5;
// ilo_exposure (ILO 2025) is a 0..4 gradient.
const ILO_MAX = 4;

// Shares are sliders that may sum to 120 after rounding, or to 0 when untouched:
// > 0 is normalized to 100, 0 is rejected with an explicit error (never NaN).
function normalizeTime(time) {
  if (!time || typeof time !== 'object') throw new RangeError('time shares are required');
  const values = {};
  let sum = 0;
  for (const id of TASK_IDS) {
    const v = Number(time[id] ?? 0);
    if (!Number.isFinite(v) || v < 0) throw new RangeError(`time share ${id} must be a number >= 0`);
    values[id] = v;
    sum += v;
  }
  if (sum <= 0) throw new RangeError('time shares must sum to 100 (all shares are zero)');
  const out = {};
  for (const id of TASK_IDS) out[id] = (values[id] / sum) * 100;
  return out;
}

function roleExposure(role) {
  const msRaw = Number(role && role.ms_applicability);
  const iloRaw = Number(role && role.ilo_exposure);
  if (!Number.isFinite(msRaw) || !Number.isFinite(iloRaw)) {
    throw new RangeError(`role ${role && role.role_id} has invalid exposure data`);
  }
  const ms = Math.min(1, Math.max(0, msRaw / MS_SATURATION));
  const ilo = Math.min(1, Math.max(0, iloRaw / ILO_MAX));
  return 0.5 * ms + 0.5 * ilo;
}

function bandFor(value) {
  if (!Number.isFinite(value)) throw new RangeError('score must be a finite number');
  if (value < 35) return 'low';
  if (value < 55) return 'moderate';
  if (value < 75) return 'high';
  return 'critical';
}

function pathFor(value, experience, signalsCount) {
  if (!Number.isFinite(value)) throw new RangeError('score must be a finite number');
  // CRO audit round 6: a critical score with two or more organisational signals means Path C whatever the experience
  // (the experienced readers most at risk need the move, not 'keep the role'); then experience (15+ years) or a score
  // under 55 means Path A; otherwise B. Module 0 (§m0-paths) states the same order.
  if (value >= 75 && signalsCount >= 2) return 'c';
  if (value < 55 || experience >= 2) return 'a';
  return 'b';
}

function checkBucket(name, v) {
  if (!Number.isInteger(v) || v < 0 || v > 3) throw new RangeError(`${name} must be an integer 0..3`);
  return v;
}

// Step 4 has no preselected answer (CRO audit round 2): the result is shown only when the role, a positive time
// split, the AI use and the years of experience are all answered. ui.js applies the same rule to the button.
function isComplete(answers) {
  if (!answers || typeof answers !== 'object' || !answers.role_id) return false;
  const isBucket = (v) => Number.isInteger(v) && v >= 0 && v <= 3;
  if (!isBucket(answers.ai_use) || !isBucket(answers.experience_years)) return false;
  const time = answers.time || {};
  return TASK_IDS.some((id) => Number(time[id]) > 0);
}

function score(answers, data) {
  if (!answers || typeof answers !== 'object') throw new RangeError('answers are required');
  const roles = data && Array.isArray(data.roles) ? data.roles : [];
  const role = roles.find((r) => r.role_id === answers.role_id);
  if (!role) throw new Error(`unknown role ${answers.role_id}`);
  if (!Array.isArray(answers.signals) || answers.signals.length !== 3) throw new RangeError('signals must be an array of 3 booleans');
  const aiUse = checkBucket('ai_use', answers.ai_use);
  const experience = checkBucket('experience_years', answers.experience_years);
  const time = normalizeTime(answers.time);
  const signalsCount = answers.signals.filter(Boolean).length;

  const timeExposure = TASK_IDS.reduce((acc, id) => acc + (time[id] / 100) * TASK_EXPOSURE[id], 0);
  const raw = WEIGHT_ROLE * roleExposure(role) + WEIGHT_TIME * timeExposure + WEIGHT_SIGNALS * (signalsCount / 3);
  const adjusted = Math.round(raw * 100) - PENALTY_AI_USE * aiUse - PENALTY_EXPERIENCE * experience;
  const value = Math.max(0, Math.min(100, adjusted));

  const topTasks = TASK_IDS
    .map((id) => ({ id, exposed: time[id] * TASK_EXPOSURE[id] }))
    .filter((t) => t.exposed > 0)
    .sort((a, b) => b.exposed - a.exposed)
    .slice(0, 3)
    .map((t) => t.id);

  const band = bandFor(value);
  const path = pathFor(value, experience, signalsCount);
  return { score: value, band, path, top_tasks: topTasks, action_ids: ACTIONS_BY_PATH[path].slice() };
}

return { score: score, isComplete: isComplete, bandFor: bandFor, pathFor: pathFor, normalizeTime: normalizeTime, roleExposure: roleExposure, TASK_IDS: TASK_IDS, TASK_EXPOSURE: TASK_EXPOSURE, ACTIONS_BY_PATH: ACTIONS_BY_PATH };
})();
window.CFQuiz.data = {"roles":[{"role_id":"customer-service-rep","family":"customer-service","ms_applicability":0.33,"ilo_exposure":4},{"role_id":"call-center-agent","family":"customer-service","ms_applicability":0.33,"ilo_exposure":4},{"role_id":"admin-assistant","family":"admin","ms_applicability":0.31,"ilo_exposure":3},{"role_id":"executive-assistant","family":"admin","ms_applicability":0.3,"ilo_exposure":3},{"role_id":"data-entry-clerk","family":"admin","ms_applicability":0.33,"ilo_exposure":4},{"role_id":"receptionist","family":"admin","ms_applicability":0.24,"ilo_exposure":3},{"role_id":"bookkeeper","family":"finance","ms_applicability":0.3,"ilo_exposure":4},{"role_id":"accountant","family":"finance","ms_applicability":0.28,"ilo_exposure":3},{"role_id":"payroll-clerk","family":"finance","ms_applicability":0.3,"ilo_exposure":4},{"role_id":"financial-analyst","family":"finance","ms_applicability":0.32,"ilo_exposure":3},{"role_id":"bank-teller","family":"finance","ms_applicability":0.28,"ilo_exposure":4},{"role_id":"insurance-claims-adjuster","family":"finance","ms_applicability":0.3,"ilo_exposure":3},{"role_id":"paralegal","family":"legal","ms_applicability":0.3,"ilo_exposure":3},{"role_id":"lawyer","family":"legal","ms_applicability":0.22,"ilo_exposure":2},{"role_id":"hr-generalist","family":"hr","ms_applicability":0.27,"ilo_exposure":3},{"role_id":"recruiter","family":"hr","ms_applicability":0.28,"ilo_exposure":3},{"role_id":"corporate-trainer","family":"hr","ms_applicability":0.3,"ilo_exposure":2},{"role_id":"copywriter","family":"media","ms_applicability":0.38,"ilo_exposure":4},{"role_id":"journalist","family":"media","ms_applicability":0.36,"ilo_exposure":3},{"role_id":"editor","family":"media","ms_applicability":0.37,"ilo_exposure":3},{"role_id":"translator-interpreter","family":"media","ms_applicability":0.42,"ilo_exposure":4},{"role_id":"graphic-designer","family":"media","ms_applicability":0.3,"ilo_exposure":3},{"role_id":"marketing-specialist","family":"marketing","ms_applicability":0.34,"ilo_exposure":3},{"role_id":"social-media-manager","family":"marketing","ms_applicability":0.34,"ilo_exposure":3},{"role_id":"market-research-analyst","family":"marketing","ms_applicability":0.33,"ilo_exposure":3},{"role_id":"sales-rep-b2b","family":"sales","ms_applicability":0.35,"ilo_exposure":3},{"role_id":"retail-sales-associate","family":"sales","ms_applicability":0.2,"ilo_exposure":2},{"role_id":"real-estate-agent","family":"sales","ms_applicability":0.26,"ilo_exposure":2},{"role_id":"travel-agent","family":"sales","ms_applicability":0.32,"ilo_exposure":4},{"role_id":"data-analyst","family":"it","ms_applicability":0.33,"ilo_exposure":3},{"role_id":"software-developer","family":"it","ms_applicability":0.3,"ilo_exposure":3},{"role_id":"it-support-helpdesk","family":"it","ms_applicability":0.31,"ilo_exposure":3},{"role_id":"systems-administrator","family":"it","ms_applicability":0.25,"ilo_exposure":2},{"role_id":"project-manager","family":"management","ms_applicability":0.28,"ilo_exposure":2},{"role_id":"middle-manager-ops","family":"management","ms_applicability":0.24,"ilo_exposure":2},{"role_id":"logistics-coordinator","family":"operations","ms_applicability":0.29,"ilo_exposure":3},{"role_id":"procurement-buyer","family":"operations","ms_applicability":0.29,"ilo_exposure":3},{"role_id":"quality-inspector","family":"operations","ms_applicability":0.12,"ilo_exposure":1},{"role_id":"warehouse-worker","family":"operations","ms_applicability":0.05,"ilo_exposure":0},{"role_id":"truck-driver","family":"operations","ms_applicability":0.06,"ilo_exposure":0},{"role_id":"teacher-school","family":"education","ms_applicability":0.22,"ilo_exposure":2},{"role_id":"university-lecturer","family":"education","ms_applicability":0.31,"ilo_exposure":2},{"role_id":"librarian","family":"education","ms_applicability":0.28,"ilo_exposure":3},{"role_id":"nurse","family":"health","ms_applicability":0.08,"ilo_exposure":1},{"role_id":"home-health-aide","family":"health","ms_applicability":0.04,"ilo_exposure":0},{"role_id":"physician","family":"health","ms_applicability":0.15,"ilo_exposure":1},{"role_id":"pharmacist","family":"health","ms_applicability":0.18,"ilo_exposure":2},{"role_id":"social-worker","family":"health","ms_applicability":0.14,"ilo_exposure":1},{"role_id":"electrician","family":"trades","ms_applicability":0.05,"ilo_exposure":0},{"role_id":"plumber","family":"trades","ms_applicability":0.05,"ilo_exposure":0},{"role_id":"construction-worker","family":"trades","ms_applicability":0.04,"ilo_exposure":0},{"role_id":"plant-machine-operator","family":"trades","ms_applicability":0.04,"ilo_exposure":0},{"role_id":"chef-cook","family":"hospitality","ms_applicability":0.06,"ilo_exposure":0},{"role_id":"hairdresser-beautician","family":"hospitality","ms_applicability":0.04,"ilo_exposure":0},{"role_id":"security-guard","family":"services","ms_applicability":0.07,"ilo_exposure":0}],"actionOrder":["m4-day1","m4-day2","m4-day3","m4-day5","m4-day7"],"taskOrder":["info","writing","data","customer","physical","people"]};
/* Quiz controller. Browser only. Depends on window.CFQuiz (score + data) and the markup of
   sections/cf-quiz.liquid. No copy here: every string comes from the data-cf-strings JSON that
   Liquid renders from the locales (cf.quiz.*). Plain script, no ESM: quiz/build.mjs appends this
   file after score.js and the role data in assets/cf-quiz.js.
   Flow (spec §6.7): intro → 4 steps (role, time shares, company signals, AI use + experience) →
   result shown without email → native customer form with tags quiz-score-<band>, quiz-lang-<lang>,
   quiz-path-<path>. Answers live in sessionStorage['cf-quiz'] before the POST and the result is
   rebuilt from them when Shopify returns with ?customer_posted=true. */
(function () {
  if (typeof document === 'undefined') return;
  var root = document.querySelector('[data-cf-quiz]');
  var Q = window.CFQuiz;
  if (!root || !Q) return;
  var stringsEl = root.querySelector('[data-cf-strings]');
  if (!stringsEl) return;
  var strings = JSON.parse(stringsEl.textContent);
  var lang = root.getAttribute('data-lang') || 'en';
  var KEY = 'cf-quiz';
  var views = {};
  root.querySelectorAll('[data-cf-view]').forEach(function (el) { views[el.getAttribute('data-cf-view')] = el; });
  var form = views.steps;
  if (!form || !views.intro || !views.result) return;
  var steps = Array.prototype.slice.call(form.querySelectorAll('[data-cf-step]'));
  var nextBtn = form.querySelector('[data-cf-next]');
  var backBtn = form.querySelector('[data-cf-back]');
  var errorEl = form.querySelector('[data-cf-error]');
  var progress = form.querySelector('[data-cf-progress]');
  var current = 0;

  function show(name) {
    Object.keys(views).forEach(function (k) { views[k].hidden = k !== name; });
  }
  function focusHeading(view) {
    var h = view.querySelector('[data-cf-focus]');
    if (h) h.focus();
  }
  function showStep(i) {
    current = i;
    steps.forEach(function (s, j) { s.hidden = j !== i; });
    backBtn.hidden = i === 0;
    nextBtn.textContent = i === steps.length - 1 ? strings.see_result : strings.next;
    progress.textContent = strings.step_of.replace('{step}', String(i + 1)).replace('{total}', String(steps.length));
    errorEl.hidden = true;
    errorEl.textContent = '';
    form.querySelectorAll('[aria-invalid]').forEach(function (el) { el.removeAttribute('aria-invalid'); el.removeAttribute('aria-describedby'); });
    var first = steps[i].querySelector('select, input');
    if (first) first.focus();
  }
  // Step 2: one choice per task type (0 none, 1 a little, 2 some, 3 a lot). The levels are the time shares:
  // score.js normalizes any positive set to 100, so nobody has to make six sliders add up (CRO audit).
  function readTime() {
    var time = {};
    Q.TASK_IDS.forEach(function (id) {
      var el = form.querySelector('[name="time_' + id + '"]:checked');
      time[id] = el ? Number(el.value) : 0;
    });
    return time;
  }
  function timeSum() {
    var time = readTime();
    return Q.TASK_IDS.reduce(function (s, id) { return s + time[id]; }, 0);
  }
  // Analytics (spec §7, campaigns optimise on quiz_complete): Shopify customer events, read by the pixels after consent.
  function track(name, data) {
    try { if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish(name, data || {}); } catch (e) { /* analytics never blocks the quiz */ }
  }
  // Message match: show the intro line of the ad's angle (utm_content = <lang>-<angle>-<creative>…).
  (function showAngle() {
    var m = /[?&]utm_content=([^&#]+)/.exec(window.location.search);
    if (!m) return;
    var id = decodeURIComponent(m[1]);
    var a = /^[a-z]{2}(?:-[A-Za-z]{2})?-([a-z0-9]+)-/.exec(id);
    var line = a && root.querySelector('[data-cf-angle="' + a[1] + '"]');
    if (line) line.hidden = false;
  })();
  function readAnswers() {
    var roleIndex = form.querySelector('[name="role"]').value;
    var role = roleIndex === '' ? null : Q.data.roles[Number(roleIndex)];
    var signals = [0, 1, 2].map(function (i) {
      var el = form.querySelector('[name="signal_' + i + '"]');
      return !!(el && el.checked);
    });
    var ai = form.querySelector('[name="ai_use"]:checked');
    var exp = form.querySelector('[name="experience"]:checked');
    return {
      role_index: roleIndex,
      role_id: role ? role.role_id : null,
      time: readTime(),
      signals: signals,
      // No default on step 4 (CRO audit round 2): null until answered, validate(3) blocks the result.
      ai_use: ai ? Number(ai.value) : null,
      experience_years: exp ? Number(exp.value) : null
    };
  }
  function validate(i) {
    var a = readAnswers();
    if (i === 0 && !a.role_id) return strings.error_role;
    if (i === 1 && timeSum() === 0) return strings.error_sum;
    if (i === 3 && !Q.isComplete(a)) return strings.error_answers;
    return null;
  }

  // The field in error is marked and described by the message, and gets the focus (WCAG 3.3.1, CRO audit round 2).
  function invalidField(i) {
    var a = readAnswers();
    if (i === 0) return form.querySelector('[name="role"]');
    if (i === 3) {
      if (a.ai_use === null) return form.querySelector('[name="ai_use"]');
      return form.querySelector('[name="experience"]');
    }
    return steps[i] ? steps[i].querySelector('input, select') : null;
  }
  function showError(text, i) {
    errorEl.textContent = text;
    errorEl.hidden = false;
    var field = i === undefined ? null : invalidField(i);
    if (field) {
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', errorEl.id || 'cf-quiz-error');
      field.focus();
    }
  }
  function save(answers) {
    try { sessionStorage.setItem(KEY, JSON.stringify(answers)); } catch (e) { /* storage blocked: the result is still shown */ }
  }
  function load() {
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }
  function li(text) {
    var el = document.createElement('li');
    el.textContent = text;
    return el;
  }
  function fill(parent, items) {
    while (parent.firstChild) parent.removeChild(parent.firstChild);
    items.forEach(function (text) { parent.appendChild(li(text)); });
  }
  function render(answers) {
    var result = Q.score(answers, Q.data);
    var r = views.result;
    r.querySelector('[data-cf-score]').textContent = String(result.score);
    r.querySelector('[data-cf-band-title]').textContent = strings.bands[result.band].title;
    r.querySelector('[data-cf-band-text]').textContent = strings.bands[result.band].text;
    r.querySelector('[data-cf-path-title]').textContent = strings.paths[result.path].title;
    r.querySelector('[data-cf-path-text]').textContent = strings.paths[result.path].text;
    fill(r.querySelector('[data-cf-top-tasks]'), result.top_tasks.map(function (id) { return strings.tasks[Q.TASK_IDS.indexOf(id)]; }));
    fill(r.querySelector('[data-cf-actions]'), result.action_ids.map(function (id) { return strings.actions[Q.data.actionOrder.indexOf(id)]; }));
    var tags = r.querySelector('[data-cf-tags]');
    if (tags) tags.value = 'quiz-score-' + result.band + ', quiz-lang-' + lang + ', quiz-path-' + result.path;
    show('result');
    focusHeading(r);
    return result;
  }
  function restore(answers) {
    var sel = form.querySelector('[name="role"]');
    if (sel && answers.role_index !== undefined) sel.value = answers.role_index;
    Q.TASK_IDS.forEach(function (id) {
      var v = answers.time ? Math.max(0, Math.min(3, Math.round(Number(answers.time[id]) || 0))) : 0;
      var el = form.querySelector('[name="time_' + id + '"][value="' + v + '"]');
      if (el) el.checked = true;
    });
    [0, 1, 2].forEach(function (i) {
      var el = form.querySelector('[name="signal_' + i + '"]');
      if (el) el.checked = !!(answers.signals && answers.signals[i]);
    });
    var ai = form.querySelector('[name="ai_use"][value="' + answers.ai_use + '"]');
    if (ai) ai.checked = true;
    var exp = form.querySelector('[name="experience"][value="' + answers.experience_years + '"]');
    if (exp) exp.checked = true;
  }

  root.querySelector('[data-cf-start]').addEventListener('click', function () {
    track('quiz_start', { lang: lang });
    show('steps');
    showStep(0);
  });
  form.addEventListener('input', function () { save(readAnswers()); });
  form.addEventListener('change', function () { save(readAnswers()); });
  form.addEventListener('submit', function (e) { e.preventDefault(); });
  nextBtn.addEventListener('click', function () {
    var err = validate(current);
    if (err) { showError(err, current); return; }
    if (current < steps.length - 1) { showStep(current + 1); return; }
    var answers = readAnswers();
    save(answers);
    try {
      var result = render(answers);
      // Only the language leaves the page (privacy §1/§4: answers, band and path stay in the customer tags; CRO audit round 2).
      track('quiz_complete', { lang: lang });
    } catch (e) { showError(strings.error_sum); }
  });
  backBtn.addEventListener('click', function () { if (current > 0) showStep(current - 1); });
  var restartBtn = root.querySelector('[data-cf-restart]');
  if (restartBtn) {
    restartBtn.addEventListener('click', function () {
      try { sessionStorage.removeItem(KEY); } catch (e) { /* ignore */ }
      form.reset();
      show('steps');
      showStep(0);
    });
  }

  var leadForm = root.querySelector('#cf-leadform');
  if (leadForm) leadForm.addEventListener('submit', function () {
    // Optional news consent (box 2): the quiz-news tag is the only tag the news flow sends to.
    var news = leadForm.querySelector('[data-cf-news]');
    var tags = leadForm.querySelector('[data-cf-tags]');
    if (news && news.checked && tags && tags.value.indexOf('quiz-news') === -1) tags.value = tags.value ? tags.value + ', quiz-news' : 'quiz-news';
  });

  var saved = load();
  var postedMsg = root.querySelector('[data-cf-posted]');
  // The email form came back with errors (invalid email, hCaptcha, Shopify error): the URL has no customer_posted,
  // the errors are printed inside the result view, so the result must be shown again (CRO audit round 2).
  var leadErr = root.querySelector('[data-cf-lead-errors]');
  var posted = window.location.search.indexOf('customer_posted=true') > -1 || !!postedMsg;
  // CRO audit round 4: the opt-in is counted when Shopify comes back with the success message (form.posted_successfully),
  // once per session, not on submit: a submit can fail, meet hCaptcha or be lost in the POST navigation.
  if (postedMsg) {
    var OPTIN_KEY = 'cf-quiz-optin-sent';
    var sent = false;
    try { sent = sessionStorage.getItem(OPTIN_KEY) === '1'; } catch (e) { /* storage blocked */ }
    if (!sent) {
      track('quiz_optin', { lang: lang });
      try { sessionStorage.setItem(OPTIN_KEY, '1'); } catch (e) { /* ignore */ }
    }
  }
  if (saved && saved.role_id) {
    restore(saved);
    if (posted || leadErr) {
      try {
        render(saved);
        // After the email form: the error, or the "check your inbox" message, is brought into view and focused
        // (CRO audit round 3: it sat ~600px below the heading, and without the confirmation there is no Day 1 PDF).
        var target = leadErr || postedMsg;
        if (target) {
          if (target.scrollIntoView) target.scrollIntoView({ block: 'center' });
          target.focus();
        }
      } catch (e) { show('intro'); }
    } else {
      show('intro');
    }
  } else {
    if (posted && postedMsg) views.intro.insertBefore(postedMsg.cloneNode(true), views.intro.firstChild);
    show('intro');
  }
})();
